import { useEffect, useRef, useState } from "react";

import { Locales } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { FieldValues, Path, UseFormSetValue } from "react-hook-form";

import { useTranslationActivityRegistration } from "../Context/TranslationActivityContext";
import { hasTranslationContent } from "../helpers/translationContent";
import { useTranslationWriteGuard } from "../helpers/useTranslationWriteGuard";
import { requestTranslationSlot } from "../translationSlots";

import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";

export function useTranslateField<T extends FieldValues>({
  fieldName,
  defaultFieldValue,
  defaultTranslationFieldValue,
  setValue,
  translationLocale,
  skipTranslation = false,
  isPlainText = false,
}: {
  fieldName: Path<T>;
  defaultFieldValue: string | undefined | null;
  defaultTranslationFieldValue: string | undefined | null;
  setValue: UseFormSetValue<T>;
  translationLocale: Locales;
  skipTranslation?: boolean;
  isPlainText?: boolean;
}) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const guard = useTranslationWriteGuard<T>();
  const startTranslation = useTranslationActivityRegistration();
  const initial = useRef({
    fieldName,
    source: defaultFieldValue ?? "",
    saved: defaultTranslationFieldValue,
    translationLocale,
    skipTranslation,
    isPlainText,
    setValue,
    guard,
    enqueueSnackbar,
    errorMessage: t("dashboard", "formTranslation.alertMissingTranslations"),
  });
  const [isTranslating, setIsTranslating] = useState(
    !skipTranslation &&
      hasTranslationContent(defaultFieldValue, isPlainText) &&
      !hasTranslationContent(defaultTranslationFieldValue, isPlainText) &&
      !hasTranslationContent(guard.getValue(fieldName), isPlainText)
  );
  const [subscriptionVersion, setSubscriptionVersion] = useState<number | null>(null);
  const generation = useRef(0);
  const activeRequest = useRef<{
    generation: number;
    text: string;
    finish: (failed: boolean) => void;
  } | null>(null);

  trpc.translateTextStream.useSubscription(
    {
      textToTranslate: initial.current.source,
      targetLanguage: initial.current.translationLocale,
      isPlainText: initial.current.isPlainText,
    },
    {
      enabled: subscriptionVersion !== null,
      onData: (event) => {
        const request = activeRequest.current;
        if (!request || request.generation !== subscriptionVersion) {
          return;
        }
        const session = initial.current;
        if (event.type === "error") {
          request.finish(true);
          return;
        }
        const text = event.type === "chunk" ? request.text + event.content : event.translatedText;
        request.text = text;
        const written = session.guard.write(session.fieldName, text, (path, value) =>
          session.setValue(path as Path<T>, value as never)
        );
        if (!written || event.type === "done") {
          request.finish(event.type === "done" && !hasTranslationContent(text, session.isPlainText));
        }
      },
      onError: () => {
        const request = activeRequest.current;
        if (request?.generation === subscriptionVersion) {
          request.finish(true);
        }
      },
    }
  );

  useEffect(() => {
    const session = initial.current;
    if (
      session.skipTranslation ||
      !session.guard.prepare({
        key: "text",
        path: session.fieldName,
        source: session.source,
        saved: session.saved,
        isPlainText: session.isPlainText,
      })
    ) {
      setIsTranslating(false);
      return;
    }
    generation.current += 1;
    const requestGeneration = generation.current;
    const stopActivity = startTranslation();
    const pendingTimeout: { handle?: number } = {};
    const finish = (failed: boolean) => {
      if (activeRequest.current?.generation !== requestGeneration) {
        return;
      }
      activeRequest.current = null;
      window.clearTimeout(pendingTimeout.handle);
      leaveQueue();
      stopActivity();
      setSubscriptionVersion(null);
      setIsTranslating(false);
      if (failed) {
        session.enqueueSnackbar(session.errorMessage, {
          vibe: "danger",
          preventDuplicate: true,
        });
      }
    };
    activeRequest.current = { generation: requestGeneration, text: "", finish };
    setIsTranslating(true);
    const leaveQueue = requestTranslationSlot(() => {
      pendingTimeout.handle = window.setTimeout(() => finish(true), 30_000);
      setSubscriptionVersion(requestGeneration);
    });
    return () => {
      activeRequest.current = null;
      window.clearTimeout(pendingTimeout.handle);
      leaveQueue();
      stopActivity();
    };
  }, [startTranslation]);

  return { isTranslating };
}
